// src/routes/routeConfig.ts
import { lazy } from "react";

// Lazy loaded components
const VideoLayout = lazy(() => import("../components/newconference/VideoLayout"));
const Layout = lazy(() => import("../components/Layout"));
const LoginPage = lazy(() => import("../components/Login/Login"));
const LandingPage = lazy(
  () => import("../components/Landing_Page/MainContent")
);
const ContactUs = lazy(() => import("../components/ContactUs"));
const AboutUs = lazy(() => import("../components/AboutUs"));
const PricingPlans = lazy(() => import("../components/Pricing"));
const CoursesPage = lazy(() => import("../components/Landing_Page/CoursePage"));
const StudentEmailSearch = lazy(
  () => import("../components/Landing_Page/FindStudent")
);
const UnauthorizedAccess = lazy(
  () => import("../components/UnauthorizedAccess")
);


// Dashboard layouts
const StudentDashboardLayout = lazy(
  () => import("../components/studentdashboard/StudentLayout")
);
const TeachersDashboardLayout = lazy(
  () => import("../components/teachersdashboard/teachersLayout")
);
const OrganizationLayout = lazy(
  () => import("../components/organisationdashboard/OrganisationLayout")
);

// Student dashboard components
const StudentDashboardHome = lazy(
  () => import("../components/studentdashboard/StudentHome")
);
const StudentCourses = lazy(
  () => import("../components/studentdashboard/StudentCourses")
);
const CourseDetails = lazy(
  () => import("../components/studentdashboard/StudentCourseDetails")
);
const SessionSchedule = lazy(
  () => import("../components/studentdashboard/SessionScheduled")
);
const StudentTasks = lazy(
  () => import("../components/studentdashboard/StudentTasks")
);
const TaskDetails = lazy(
  () => import("../components/studentdashboard/TaskDetails")
);
const LibrarySection = lazy(
  () => import("../components/studentdashboard/StudentLibrary")
);
const StudentProfile = lazy(
  () => import("../components/studentdashboard/StudentProfile")
);
const StudentTest = lazy(
  () => import("../components/studentdashboard/StudentTest")
);
const StudentTemplateGrid = lazy(
  () => import("../components/studentdashboard/StudentTemplateGrid")
);
const TestResultsPage = lazy(
  () => import("../components/studentdashboard/TestResultsSummary")
);
const TestResult = lazy(
  () => import("../components/studentdashboard/TestResult")
);
const TestSummaryPage = lazy(
  () => import("../components/studentdashboard/TestResultsSummary")
);

// Teacher dashboard components
const TeacherDashboardHome = lazy(
  () => import("../components/teachersdashboard/TutorHome")
);
const Courses = lazy(() => import("../components/teachersdashboard/Courses"));
const TeachersCourseDetails = lazy(
  () => import("../components/teachersdashboard/CourseDetails")
);
const QATemplateBuilder = lazy(
  () => import("../components/teachersdashboard/QATemplate")
);
const CourseForm = lazy(
  () => import("../components/teachersdashboard/CourseForm")
);
const TaskList = lazy(() => import("../components/teachersdashboard/Task"));
const TaskSubmissions = lazy(
  () => import("../components/teachersdashboard/TaskSubmissions")
);
const ViewSubmission = lazy(
  () => import("../components/teachersdashboard/ViewSubmision")
);
const DigitalLibrary = lazy(
  () => import("../components/teachersdashboard/Library")
);
const UpcomingClasses = lazy(
  () => import("../components/teachersdashboard/Schedule")
);
const TutorProfile = lazy(
  () => import("../components/teachersdashboard/TutorProfile")
);
const TeacherTemplateGrid = lazy(
  () => import("../components/teachersdashboard/TeachersTemplateGrid")
);
const PaymentAccountSetup = lazy(
  () => import("../components/payments/PaymentSetUp")
);
const StripeOnboardingComplete = lazy(
  () => import("../components/payments/StripeOnboardingComplete")
);
const AnnouncementsList = lazy(
  () => import("../components/announcement/AnnouncementList")
);

// Organization dashboard components
const OrganizationHome = lazy(
  () => import("../components/organisationdashboard/OrganisationDashboard")
);
const StudentManagement = lazy(
  () => import("../components/organisationdashboard/StudentManagement")
);
const TeachersManagement = lazy(
  () => import("../components/organisationdashboard/TeachersManagement")
);
const AnalyticsPage = lazy(
  () => import("../components/organisationdashboard/Analytics")
);
const CourseManagement = lazy(
  () => import("../components/organisationdashboard/CourseManagement")
);
const SettingsPage = lazy(
  () => import("../components/organisationdashboard/Settings")
);

// Payment components
const CourseCheckout = lazy(
  () => import("../components/payments/CourseCheckout")
);
const PaymentProcessingPage = lazy(
  () => import("../components/payments/PaymentProcessing")
);
const PaymentSuccessPage = lazy(
  () => import("../components/payments/PaymentSuccess")
);
const PaymentFailedPage = lazy(
  () => import("../components/payments/PaymentFailed")
);

// Video dashboard components
const VideoDashboard = lazy(
  () => import("../components/newconference/Rooms")
)

const Whiteboard = lazy(
  () => import("../components/whiteboard/VirtualWhiteboard")
)


const ReportCards = lazy(() => import("../components/ReportCards"));
const StudentReportPage = lazy(() => import("../components/studentdashboard/StudentReportCard"));
const CertificatesPage = lazy(() => import("../components/CertificateGenerator"));

// Route configuration
export const routes = [
  // Public routes
  {
    path: "/",
    element: Layout,
    children: [
      { index: true, element: LandingPage },
      { path: "pricing", element: PricingPlans },
      { path: "about-us", element: AboutUs },
      { path: "contact-us", element: ContactUs },
      { path: "courses", element: CoursesPage },
      { path: "courses/find-student", element: StudentEmailSearch },
      { path: "login", element: LoginPage },
      { path: "unauthorized", element: UnauthorizedAccess },
      {
        path: "signup/:role",
        element: "REGISTER_FORM",
        // This is a special case where we determine the element dynamically
      },
      // Payment routes
      {
        path: "course/:courseId/checkout",
        element: CourseCheckout,
        wrapWithElements: true,
      },
      {
        path: "courses/:courseId/processing/:paymentId",
        element: PaymentProcessingPage,
      },
      { path: "courses/:courseId/success", element: PaymentSuccessPage },
      { path: "courses/:courseId/failed", element: PaymentFailedPage },
    ],
  },

  // Student dashboard routes
  {
    path: "studentdashboard",
    element: StudentDashboardLayout,
    children: [
      { index: true, element: StudentDashboardHome },
      { path: "courses", element: StudentCourses },
      { path: ":courseId/course-details", element: CourseDetails },
      { path: "sessions", element: SessionSchedule },
      { path: "tasks", element: StudentTasks },
      { path: "task-details", element: TaskDetails },
      { path: "library", element: LibrarySection },
      { path: "profile", element: StudentProfile },
      { path: "test/:templateId", element: StudentTest },
      { path: "qa/templates", element: StudentTemplateGrid },
      { path: "qa", element: StudentTest },
      { path: "test-results", element: TestResultsPage },
      { path: "test-summary/:testHistoryId", element: TestSummaryPage },
      { path: "test-result/:sessionId", element: TestResult },
      { path: "certificates", element: CertificatesPage },
    ],
  },

  // Teacher dashboard routes
  {
    path: "teachersdashboard",
    element: TeachersDashboardLayout,
    children: [
      { index: true, element: TeacherDashboardHome },
      { path: "courses", element: Courses },
      { path: "course-details", element: TeachersCourseDetails },
      { path: "qa/create", element: QATemplateBuilder },
      { path: "courses/form", element: CourseForm },
      { path: "tasks", element: TaskList },
      { path: "tasks/submissions", element: TaskSubmissions },
      { path: "tasks/view-submissions", element: ViewSubmission },
      { path: "library", element: DigitalLibrary },
      { path: "schedule", element: UpcomingClasses },
      { path: "profile", element: TutorProfile },
      { path: "qa/templates", element: TeacherTemplateGrid },
      { path: "payment-setup", element: PaymentAccountSetup },
      { path: "stripe/complete-onboarding", element: StripeOnboardingComplete },
      { path: "announcement/list", element: AnnouncementsList },
      { path: "report-card/:studentId/:courseId", element: ReportCards },
      { path: "report-card/:studentId/", element: StudentReportPage },
      { path: "certificate/:studentId/:courseId/:tutorId/:studentEmail", element: CertificatesPage },

    ],
  },

  // Organization dashboard routes
  {
    path: "organization",
    element: OrganizationLayout,
    children: [
      { path: "dashboard", element: OrganizationHome },
      { path: "students", element: StudentManagement },
      { path: "teachers", element: TeachersManagement },
      { path: "analytics", element: AnalyticsPage },
      { path: "courses", element: CourseManagement },
      { path: "settings", element: SettingsPage },
      { path: "payment-setup", element: PaymentAccountSetup },
      { path: "stripe/complete-onboarding", element: StripeOnboardingComplete },
      { path: "org/announcement/list", element: AnnouncementsList },
      { path: "certificate/:studentId/:courseId/:organizationId/:studentEmail", element: CertificatesPage },
    ],
  },
  {
    path: "conference",
    element: VideoLayout,
    children: [
      { path: "classroom/:roomId", element: VideoDashboard },
      { path: "whiteboard/:roomId", element: Whiteboard },
    ],
  },
];
